// Fuzzball Quiz Bundle Builder
// Custom element for managing subscription product selections

class QuizSubscriptionBuilder extends HTMLElement {
  constructor() {
    super();
    this.rechargeAuth = null;
    this.rechargeProducts = {};
    this.collections = {};
    this.recommendations = null;
    this.selections = {}; // Changed to object to store per-cat selections
    this.currentCat = null; // Track which cat we're currently editing
    this.sellingPlanGroupId = null;
    this.sellingPlanId = null;
    this.loadingStates = {
      auth: false,
      collections: false,
      products: false
    };
  }

  connectedCallback() {
    this.initializeComponent();
  }

  async initializeComponent() {
    try {
      // Initialize Recharge
      await this.initializeRecharge();
      
      // Load collections
      await this.loadCollections();
      
      // Listen for quiz completion events
      this.setupEventListeners();
      
      // Initial render
      this.render();
      
      // Check for existing quiz recommendations
      this.checkForExistingRecommendations();
    } catch (error) {
      // Still render something even if initialization fails
      this.render();
    }
  }

  checkForExistingRecommendations() {
    // First try to load saved selections from localStorage
    const hasLoadedSelections = this.loadSelections();
    
    // Check if we're in edit mode
    const editMode = window.QuizState.get('editMode');
    if (editMode?.active) {
      // Always check if we have bundle products to restore, regardless of editType
      // This allows both systems to be populated when editing from cart
      if (editMode.products?.bundle) {
        // Wait for Recharge products to load before restoring
        this.waitForRechargeAndRestore(editMode);
        return;
      } else {
        return;
      }
    }
    
    // Only apply existing recommendations if quiz has been completed
    const quizCompleted = localStorage.getItem('quiz_submitted');
    const recommendations = window.QuizState.get('recommendations') || window.QuizState.get('quizRecommendations');
    
    if (quizCompleted === 'true' && recommendations) {
      
      // Check if we're editing a specific cat (bundle system might have been opened for a specific cat)
      const bundleSystem = document.querySelector('.bundle-system');
      const editingCat = bundleSystem ? bundleSystem.getAttribute('data-editing-cat') : null;
      
      if (editingCat) {
      }
      
      // If we loaded saved selections, don't apply default recommendations
      if (hasLoadedSelections) {
        // Restore UI from saved selections
        setTimeout(() => {
          this.restoreUIFromSelections();
        }, 100);
      } else {
        // Use setTimeout to ensure DOM is ready
        setTimeout(() => {
          this.applyQuizRecommendations(recommendations);
        }, 100);
      }
    }
  }
  
  async waitForRechargeAndRestore(editMode) {
    // Wait for Recharge products to be loaded
    const maxAttempts = 20;
    let attempts = 0;
    
    const checkAndRestore = () => {
      if (this.rechargeProducts && Object.keys(this.rechargeProducts).length > 0) {
        this.restoreEditModeProducts(editMode);
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(checkAndRestore, 500);
      } else {
      }
    };
    
    checkAndRestore();
  }
  
  restoreEditModeProducts(editMode) {
    if (!editMode.products?.bundle) {
      return;
    }
    
    
    // Clear current selections for all cats
    this.selections = {};
    
    // Clear all quantity inputs first
    const allQuantityInputs = this.querySelectorAll('.quantity-input');
    allQuantityInputs.forEach(input => {
      input.value = 0;
    });
    
    // Group products by cat if they have cat properties
    const productsByCat = {};
    editMode.products.bundle.forEach(savedProduct => {
      const catNumber = savedProduct.properties?._cat_number || savedProduct.cat_number || '1';
      if (!productsByCat[catNumber]) {
        productsByCat[catNumber] = [];
      }
      productsByCat[catNumber].push(savedProduct);
    });
    
    
    // Store the original currentCat value
    const originalCurrentCat = this.currentCat;
    
    // Restore each cat's products
    Object.entries(productsByCat).forEach(([catNumber, products]) => {
      // Temporarily set currentCat for this restoration
      this.currentCat = `cat${catNumber}`;
      
      // Initialize selections for this cat
      if (!this.selections[this.currentCat]) {
        this.selections[this.currentCat] = [];
      }
      
      products.forEach(savedProduct => {
        // Find the product in our collections
        const product = this.findProductById(savedProduct.id.toString());
        if (product) {
          this.updateSelection(product, savedProduct.quantity);
          
          // Update the quantity input in the UI
          const productCard = this.querySelector(`[data-product-id="${savedProduct.id}"]`);
          if (productCard) {
            const quantityInput = productCard.querySelector('.quantity-input');
            if (quantityInput) {
              quantityInput.value = savedProduct.quantity;
            }
          }
        } else {
        }
      });
    });
    
    // Set current cat based on edit mode or default to cat1
    const currentEditMode = window.QuizState.get('editMode');
    if (currentEditMode?.catNumber) {
      this.currentCat = `cat${currentEditMode.catNumber}`;
    } else {
      this.currentCat = originalCurrentCat || 'cat1';
    }
    
    // Log the final state of selections
    
    // Update the summary
    this.updateBundleSummary();
    
    // Important: Do NOT apply quiz recommendations after this
  }

  async initializeRecharge() {
    this.loadingStates.auth = true;
    
    try {
      if (typeof recharge === 'undefined') {
        throw new Error('Recharge object not found');
      }

      // Login to Recharge
      this.rechargeAuth = await recharge.auth.loginShopifyAppProxy();
      
      if (!this.rechargeAuth) {
        throw new Error('Failed to authenticate with Recharge');
      }
      
      
      // Load all Recharge product data at once
      const rcData = await recharge.cdn.getCDNProductsAndSettings();
      
      // Process Recharge products into a lookup map
      this.rechargeProducts = rcData.products.reduce((result, obj) => {
        const productData = Object.values(obj)[0];
        const productId = productData.external_product_id;
        result[productId] = productData;
        return result;
      }, {});
      
      console.log('✅ Recharge data loaded:', { 
        productsCount: Object.keys(this.rechargeProducts).length
      });
      
      this.loadingStates.auth = false;
    } catch (error) {
      this.loadingStates.auth = false;
      throw error;
    }
  }

  async loadCollections() {
    this.loadingStates.collections = true;
    
    const collectionIds = this.getCollectionIds();
    
    try {
      await Promise.all([
        this.loadCollection('dry', collectionIds.dry),
        this.loadCollection('wet', collectionIds.wet),
        this.loadCollection('treats', collectionIds.treats),
        this.loadCollection('litter', collectionIds.litter)
      ]);
      
      this.loadingStates.collections = false;
    } catch (error) {
      this.loadingStates.collections = false;
      throw error;
    }
  }

  getCollectionIds() {
    return {
      dry: this.getAttribute('dry-collection-id') || window.QuizConstants.COLLECTIONS.DRY_FOOD,
      wet: this.getAttribute('wet-collection-id') || window.QuizConstants.COLLECTIONS.WET_FOOD,
      treats: this.getAttribute('treats-collection-id') || window.QuizConstants.COLLECTIONS.TREATS,
      litter: this.getAttribute('litter-collection-id') || window.QuizConstants.COLLECTIONS.LITTER
    };
  }

  async loadCollection(type, collectionId) {
    if (!collectionId) {
      return;
    }

    try {
      const products = await this.fetchCollectionProducts(collectionId);
      this.collections[type] = this.withoutHiddenFlavours(products);
    } catch (error) {
    }
  }

  async fetchCollectionProducts(collectionId) {
    const query = `
      query getCollection($id: ID!) {
        collection(id: $id) {
          products(first: 50) {
            edges {
              node {
                id
                title
                handle
                productType
                tags
                images(first: 1) {
                  edges {
                    node {
                      url
                      altText
                    }
                  }
                }
                variants(first: 10) {
                  edges {
                    node {
                      id
                      title
                      price {
                        amount
                        currencyCode
                      }
                      availableForSale
                      quantityAvailable
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const response = await fetch('/api/2023-10/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': 'd6de5e4fac40b2c47afd95357052097c'
      },
      body: JSON.stringify({
        query,
        variables: {
          id: `gid://shopify/Collection/${collectionId}`
        }
      })
    });

    const data = await response.json();
    
    if (data.errors) {
      throw new Error(data.errors[0].message);
    }

    const products = data.data.collection.products.edges.map(edge => edge.node);
    
    // Enrich products with Recharge data
    const enrichedProducts = await this.enrichProductsWithRechargeData(products);
    
    return enrichedProducts;
  }

  async enrichProductsWithRechargeData(products) {
    // Since we already have all Recharge data, just enrich the products
    for (const product of products) {
      const productId = product.id.split('/').pop();
      const rechargeData = this.rechargeProducts[productId];
      
      if (rechargeData) {
        // Store selling plan info
        if (!this.sellingPlanGroupId && rechargeData.selling_plan_groups && rechargeData.selling_plan_groups.length > 0) {
          this.sellingPlanGroupId = rechargeData.selling_plan_groups[0].selling_plan_group_id;
          if (rechargeData.selling_plan_groups[0].selling_plans && rechargeData.selling_plan_groups[0].selling_plans.length > 0) {
            this.sellingPlanId = rechargeData.selling_plan_groups[0].selling_plans[0].selling_plan_id;
          }
        }
        
        // Enhance product with price (no discount applied)
        const variant = product.variants.edges[0]?.node;
        if (variant && rechargeData.selling_plan_groups && rechargeData.selling_plan_groups[0]?.selling_plans[0]) {
          const sellingPlan = rechargeData.selling_plan_groups[0].selling_plans[0];
          const regularPrice = parseFloat(variant.price.amount);
          
          // Use full price without discount
          product.subscriptionPrice = regularPrice;
          product.regularPrice = regularPrice;
          product.discount = 0; // No discount shown
          product.rechargeData = rechargeData;
        }
      }
    }
    
    return products;
  }

  setupEventListeners() {
    // Listen for quiz completion
    document.addEventListener('quizCompleted', (event) => {
      this.applyQuizRecommendations(event.detail.recommendations);
    });
    
    // Set up return to cart button if in restricted edit mode
    const editMode = window.QuizState.get('editMode');
    if (editMode?.active && editMode?.restricted) {
      setTimeout(() => this.setupReturnToCartButton(), 100);
    }

    // Quantity control listeners - handle clicks on SVG elements inside buttons
    this.addEventListener('click', (e) => {
      const plusBtn = e.target.closest('.plus-btn');
      const minusBtn = e.target.closest('.minus-btn');

      if (plusBtn) {
        // User-driven edit — mark this cat's bundle as user-edited so
        // subsequent step-7 re-entries preserve their changes instead of
        // re-applying recommendations.
        if (window.QuizState && typeof window.QuizState.markSelectionUserEdited === 'function') {
          window.QuizState.markSelectionUserEdited('bundle', this.currentCat || 'cat1');
        }
        this.handleQuantityChange(plusBtn, 1);
      } else if (minusBtn) {
        if (window.QuizState && typeof window.QuizState.markSelectionUserEdited === 'function') {
          window.QuizState.markSelectionUserEdited('bundle', this.currentCat || 'cat1');
        }
        this.handleQuantityChange(minusBtn, -1);
      }
    });

    // Handle manual input changes
    this.addEventListener('input', (e) => {
      if (e.target.classList.contains('quantity-input')) {
        const productCard = e.target.closest('.product-card');
        const productId = productCard.dataset.productId;
        const product = this.productList[productId];

        if (product) {
          const newQuantity = Math.max(0, parseInt(e.target.value) || 0);
          e.target.value = newQuantity; // Ensure clean value
          // User-driven typed-in quantity — same flag flip as +/- buttons.
          if (window.QuizState && typeof window.QuizState.markSelectionUserEdited === 'function') {
            window.QuizState.markSelectionUserEdited('bundle', this.currentCat || 'cat1');
          }
          this.updateSelection(product, newQuantity);
          this.updateBundleSummary();
        }
      }
    });

    // Listen for changes from bundle summary updates
    this.addEventListener('bundleUpdated', () => {
      this.updateBundleSummary();
    });
  }

  applyQuizRecommendations(recommendations) {
    if (!recommendations) {
      return;
    }

    console.log('%c=== 📦 BUNDLE SYSTEM AUTO-POPULATE DEBUG START ===', 'background: #2196F3; color: white; padding: 5px; font-weight: bold;');
    
    // Check if this is multi-cat recommendations
    let effectiveRecommendations = recommendations;
    if (recommendations.cats && recommendations.totals) {
      // Multi-cat structure - check which cat we're editing
      const bundleSystem = document.querySelector('.bundle-system');
      const editingCat = bundleSystem ? bundleSystem.getAttribute('data-editing-cat') : null;
      
      if (editingCat) {
        // Use specific cat's recommendations
        // Don't override currentCat if it's already set correctly by switchToCat
        if (this.currentCat !== `cat${editingCat}`) {
          this.currentCat = `cat${editingCat}`;
        }
        effectiveRecommendations = recommendations.cats[`cat${editingCat}`];
      } else if (this.currentCat && recommendations.cats[this.currentCat]) {
        // If currentCat is set (e.g., from switchToCat), use that cat's recommendations
        effectiveRecommendations = recommendations.cats[this.currentCat];
      } else {
        // Apply recommendations for all cats individually
        Object.entries(recommendations.cats).forEach(([catKey, catRec]) => {
          const catNumber = catKey.replace('cat', '');
          this.currentCat = catKey;
          
          this.recommendations = catRec;

          // Filter and select products for this specific cat
          const filteredProducts = this.filterProductsForRecommendations(catRec);
          this.autoSelectProducts(filteredProducts, catRec);
        });
        
        // Don't reset currentCat to null - keep it as the last processed cat
        
        // Update display and summary
        this.render();
        this.updateBundleSummary();
        
        console.log('%c=== 📦 BUNDLE SYSTEM DEBUG END ===\n', 'background: #2196F3; color: white; padding: 5px; font-weight: bold;');
        return; // Exit early as we've handled all cats
      }
    } else {
      // Single cat mode - default to cat 1
      if (!this.currentCat) {
        this.currentCat = 'cat1';
      }
    }
    
    
    this.recommendations = effectiveRecommendations;

    // Filter products based on recommendations
    const filteredProducts = this.filterProductsForRecommendations(effectiveRecommendations);

    // Auto-select products based on quantity needs
    this.autoSelectProducts(filteredProducts, effectiveRecommendations);

    // Update display
    this.render();
  }

  filterProductsForRecommendations(recommendations) {
    const filtered = {
      dry: [],
      wet: [],
      treats: [],
      litter: []
    };

    console.log('Starting with:', {
      totalDryProducts: this.collections.dry?.length || 0,
      totalWetProducts: this.collections.wet?.length || 0,
      dislikes: recommendations.dislikes
    });

    // Filter dry food
    if (this.collections.dry && recommendations.bagsNeeded > 0) {
      filtered.dry = this.filterDryFood(this.collections.dry, recommendations);
    }

    // Filter wet food. Strict pass first (Available flavours only); if the
    // cat's dislikes leave nothing, fall back to Deprioritised flavours rather
    // than an empty monthly plan (mirrors the trial box).
    if (this.collections.wet && recommendations.packsNeeded > 0) {
      filtered.wet = this.filterWetFood(this.collections.wet, recommendations, false);
      if (filtered.wet.length === 0) {
        filtered.wet = this.filterWetFood(this.collections.wet, recommendations, true);
        filtered.wetUsedDeprioritised = filtered.wet.length > 0;
        if (filtered.wetUsedDeprioritised) {
          console.log('📦 No Available wet flavours left after dislikes; falling back to Deprioritised flavours');
        }
      }
    }

    // Treats and litter are optional, not filtered by recommendations
    filtered.treats = this.collections.treats || [];
    filtered.litter = this.collections.litter || [];

    console.log('\n📊 BUNDLE FILTERING RESULTS:', {
      dryBeforeFilter: this.collections.dry?.length || 0,
      dryAfterFilter: filtered.dry.length,
      dryFilteredOut: (this.collections.dry?.length || 0) - filtered.dry.length,
      wetBeforeFilter: this.collections.wet?.length || 0,
      wetAfterFilter: filtered.wet.length,
      wetFilteredOut: (this.collections.wet?.length || 0) - filtered.wet.length
    });
    
    return filtered;
  }

  filterDryFood(products, recommendations) {
    
    return products.filter(product => {
      const title = product.title.toLowerCase();
      
      // Check dislikes first
      if (recommendations.dislikes && recommendations.dislikes.length > 0) {
        if (recommendations.dislikes.some(dislike => title.includes(dislike.toLowerCase()))) {
          return false;
        }
      }
      
      // Age-based filtering
      if (recommendations.isKitten) {
        // Kittens: only kitten food for dry
        if (!title.includes('kitten')) {
          return false;
        }
      } else {
        // Adults: exclude all kitten products
        if (title.includes('kitten')) {
          return false;
        }
      }

      // Lifestyle-based filtering (adults only, dry food only)
      if (!recommendations.isKitten) {
        if ((recommendations.lifestyle === 'Indoor' || recommendations.lifestyle === 'Both') && !(title.includes('chicken') || title.includes('tuna') || title.includes('salmon'))) {
          return false;
        } else if (recommendations.lifestyle === 'Outdoor' && !title.includes('turkey')) {
          return false;
        }
      }

      return true;
    });
  }

  // Flavour states (Theme settings → Quiz — Flavours & Trial Box, Sep 2026).
  // Handles of every wet product whose flavour is Deprioritised: still in the
  // customizer for the customer to add, never auto-picked. Replaces the
  // hard-coded May 2026 beef wind-down rule.
  getDeprioritisedHandles() {
    if (!this._deprioritisedHandles) {
      try {
        const raw = this.getAttribute('data-deprioritised-handles');
        this._deprioritisedHandles = new Set(raw ? JSON.parse(raw) : []);
      } catch (e) {
        this._deprioritisedHandles = new Set();
      }
    }
    return this._deprioritisedHandles;
  }

  isDeprioritised(product) {
    return !!(product && product.handle && this.getDeprioritisedHandles().has(product.handle));
  }

  // Handles of every wet product whose flavour is Hidden (Oct 2026, beef
  // discontinued): left out of the monthly box entirely, so it is neither
  // listed in Edit monthly plan nor auto-picked.
  getHiddenHandles() {
    if (!this._hiddenHandles) {
      try {
        const raw = this.getAttribute('data-hidden-handles');
        this._hiddenHandles = new Set(raw ? JSON.parse(raw) : []);
      } catch (e) {
        this._hiddenHandles = new Set();
      }
    }
    return this._hiddenHandles;
  }

  withoutHiddenFlavours(products) {
    if (!Array.isArray(products)) return products;
    const hidden = this.getHiddenHandles();
    if (hidden.size === 0) return products;
    this._hiddenProductIds = this._hiddenProductIds || new Set();
    return products.filter(product => {
      if (product && hidden.has(product.handle)) {
        this._hiddenProductIds.add(String(product.id).split('/').pop());
        return false;
      }
      return true;
    });
  }

  // A plan saved before a flavour was hidden still holds it: drop it so it
  // is not shown in the summary or sent to the cart.
  dropHiddenFromSelections() {
    const hiddenIds = this._hiddenProductIds;
    if (!hiddenIds || hiddenIds.size === 0) return;
    let changed = false;
    Object.keys(this.selections || {}).forEach(catKey => {
      const items = this.selections[catKey];
      if (!Array.isArray(items)) return;
      const kept = items.filter(item => !hiddenIds.has(String(item.product_id).split('/').pop()));
      if (kept.length !== items.length) {
        this.selections[catKey] = kept;
        changed = true;
      }
    });
    if (changed) this.saveSelections();
  }

  filterWetFood(products, recommendations, allowDeprioritised = false) {

    return products.filter(product => {
      const title = product.title.toLowerCase();

      if (!allowDeprioritised && this.isDeprioritised(product)) {
        return false;
      }

      // Check dislikes first
      if (recommendations.dislikes && recommendations.dislikes.length > 0) {
        if (recommendations.dislikes.some(dislike => title.includes(dislike.toLowerCase()))) {
          return false;
        }
      }

      // Age-based filtering
      if (recommendations.isKitten) {
        // Kittens with wet food preference: allow all wet foods
        if (recommendations.diet === 'Wet' || recommendations.wet_dry_preference === 'Wet') {
          return true;
        }
        // Otherwise, exclude specific wet food (chicken & duck)
        if (title.includes('chicken') && title.includes('duck')) {
          return false;
        }
      } else {
        // Adults: exclude all kitten products
        if (title.includes('kitten')) {
          return false;
        }
      }

      // No lifestyle filtering for wet food

      return true;
    });
  }


  hasDislikedFlavor(product, dislikes) {
    if (!dislikes || dislikes.length === 0) return false;
    
    const productTitle = product.title.toLowerCase();
    
    return dislikes.some(dislike => {
      const dislikeLower = dislike.toLowerCase();
      return productTitle.includes(dislikeLower);
    });
  }

  // Filter methods without dislike checking for priority override
  filterDryFoodWithoutDislikes(products, recommendations) {
    
    return products.filter(product => {
      const title = product.title.toLowerCase();
      
      // Age-based filtering
      if (recommendations.isKitten) {
        // Kittens: only kitten food for dry
        if (!title.includes('kitten')) {
          return false;
        }
      } else {
        // Adults: exclude all kitten products
        if (title.includes('kitten')) {
          return false;
        }
      }

      // Lifestyle-based filtering (adults only, dry food only)
      if (!recommendations.isKitten) {
        if ((recommendations.lifestyle === 'Indoor' || recommendations.lifestyle === 'Both') && !(title.includes('chicken') || title.includes('tuna') || title.includes('salmon'))) {
          return false;
        } else if (recommendations.lifestyle === 'Outdoor' && !title.includes('turkey')) {
          return false;
        }
      }

      return true;
    });
  }

  filterWetFoodWithoutDislikes(products, recommendations, allowDeprioritised = false) {
    
    return products.filter(product => {
      const title = product.title.toLowerCase();

      if (!allowDeprioritised && this.isDeprioritised(product)) {
        return false;
      }
      
      // Age-based filtering
      if (recommendations.isKitten) {
        // Kittens with wet food preference: allow all wet foods
        if (recommendations.diet === 'Wet' || recommendations.wet_dry_preference === 'Wet') {
          return true;
        }
        // Otherwise, exclude specific wet food (chicken & duck)
        if (title.includes('chicken') && title.includes('duck')) {
          return false;
        }
      } else {
        // Adults: exclude all kitten products
        if (title.includes('kitten')) {
          return false;
        }
      }

      // No lifestyle filtering for wet food
      return true;
    });
  }

  autoSelectProducts(filteredProducts, recommendations) {

    // Decide whether to re-apply recommendations or preserve existing UI:
    // - User has manually edited this cat's bundle since the last reset →
    //   preserve their edits (they may have changed quantities in the edit-
    //   recipes modal). This is the case where back-then-forward must NOT
    //   clobber the user's work.
    // - No user edits yet → clear + re-apply the latest recommendations.
    //   This is the case where a quiz answer change must flow through to the
    //   bundle composition, even if the bundle was auto-populated on a prior
    //   step-7 entry (the old `currentSelections.length === 0` gate let stale
    //   auto-populated state survive answer changes — the bug we're fixing).
    const catKey = this.currentCat || 'cat1';
    const isUserEdited = !!(window.QuizState
      && window.QuizState.get
      && window.QuizState.get('selections.bundleUserEdited')?.[catKey]);

    if (!isUserEdited) {
      // Clear all existing selections first — re-apply fresh recommendations.
      this.clearAllSelections();
    } else {
      // Store current cat before restoring UI
      const preservedCat = this.currentCat;
      // Update UI to show existing selections instead of auto-selecting
      this.restoreUIFromSelections();
      // Ensure currentCat is preserved after restore
      this.currentCat = preservedCat;
      return;
    }
    
    // Select dry food
    if (recommendations.bagsNeeded > 0) {
      let dryProducts = filteredProducts.dry;
      
      // PRIORITY RULE: Dry food is essential for dental health and nutrition
      // If no products pass dislike filters, override preferences for dry food only
      if (dryProducts.length === 0 && this.collections.dry && this.collections.dry.length > 0) {
        console.log('%c⚠️ DRY FOOD DISLIKE OVERRIDE', 'background: #FF9800; color: white; padding: 3px; font-weight: bold;');
        // Re-filter without dislikes
        dryProducts = this.filterDryFoodWithoutDislikes(this.collections.dry, recommendations);
      }
      
      if (dryProducts.length > 0) {
        this.distributeProductsToInputs(dryProducts, recommendations.bagsNeeded, 'dry');
      }
    }

    // Select wet food
    if (recommendations.packsNeeded > 0) {
      const wetProducts = filteredProducts.wet;
      
      // WET FOOD: Always respect dislikes - no override
      if (wetProducts.length === 0) {
        console.log('%c⚠️ NO WET FOOD AVAILABLE', 'background: #f44336; color: white; padding: 3px; font-weight: bold;');
      } else {
        this.distributeProductsToInputs(wetProducts, recommendations.packsNeeded, 'wet');
        if (filteredProducts.wetUsedDeprioritised && window.QuizUtils && window.QuizUtils.Error) {
          window.QuizUtils.Error.show('Some flavours are limited right now, so we\'ve built your box from our other flavours');
        }
      }
    }

    // Get current cat's selections for logging
    const finalSelections = this.currentCat && this.selections[this.currentCat] ? this.selections[this.currentCat] : [];
    finalSelections.forEach(selection => {
    });
    
    console.log('1. Dislike Override:', {
      dryFoodOverridden: filteredProducts.dry.length === 0 && this.collections.dry && this.collections.dry.length > 0,
      wetFoodOverridden: false, // Wet food never overrides dislikes
      reason: 'Dry food only - nutritional needs take priority over flavor preferences'
    });
    
    console.log('2. Selection Results:', {
      targetDryBags: recommendations.bagsNeeded,
      actualDryBags: finalSelections.filter(s => s.type === 'dry').reduce((sum, s) => sum + s.quantity, 0),
      targetWetPacks: recommendations.packsNeeded,
      actualWetPacks: finalSelections.filter(s => s.type === 'wet').reduce((sum, s) => sum + s.quantity, 0)
    });
    
    console.log('3. Bundle Totals:', {
      totalItems: finalSelections.reduce((sum, s) => sum + s.quantity, 0),
      totalPrice: `£${this.getTotalPrice().toFixed(2)}`,
      monthlySubscriptionPrice: `£${this.getTotalPrice().toFixed(2)}`
    });
    
    console.log('%c=== 📦 BUNDLE SYSTEM DEBUG END ===\n', 'background: #2196F3; color: white; padding: 5px; font-weight: bold;');
    
    // Save selections after applying recommendations
    this.saveSelections();
  }

  clearAllSelections() {
    // Clear selections for current cat or all cats
    if (this.currentCat) {
      this.selections[this.currentCat] = [];
    } else {
      this.selections = {};
    }
    const quantityInputs = this.querySelectorAll('.quantity-input');
    quantityInputs.forEach(input => {
      input.value = 0;
    });
    this.updateBundleSummary();
    
    // Save cleared state
    this.saveSelections();
  }

  distributeProductsToInputs(products, targetQuantity, type) {
    if (products.length === 0) return;
    
    
    // Even distribution across the eligible products (profit ranking
    // removed Sep 2026; flavour states in theme settings decide the pool).
    const sortedProducts = [...products];

    // Get product cards for this type
    const productCards = this.getProductCardsForType(type);
    
    // Create a map of product titles to cards for faster lookup
    const titleToCard = new Map();
    productCards.forEach(card => {
      const title = card.querySelector('.product-title')?.textContent || '';
      if (title) titleToCard.set(title, card);
    });
    
    // Distribution plan: even split, remainder to the first products
    const qtyPerProduct = Math.floor(targetQuantity / sortedProducts.length);
    const remainder = targetQuantity % sortedProducts.length;
    const distributionPlan = sortedProducts.map((product, index) => ({
      product: product,
      quantity: qtyPerProduct + (index < remainder ? 1 : 0)
    }));

    let distributed = 0;

    for (const { product, quantity: plannedQty } of distributionPlan) {
      const card = titleToCard.get(product.title);
      
      if (!card) {
        continue;
      }
      
      
      const quantityInput = card.querySelector('.quantity-input');
      const productId = card.dataset.productId;
      
      if (quantityInput && productId) {
        const toAdd = plannedQty; // Bundle products don't have inventory limits
        const currentQty = parseInt(quantityInput.value) || 0;
        const newQty = currentQty + toAdd;
        
        quantityInput.value = newQty;
        const rechargeProduct = this.productList[productId];
        if (rechargeProduct) {
          this.updateSelection(rechargeProduct, newQty);
          distributed += toAdd;
        } else {
        }
      } else {
      }
    }
    
    if (distributed < targetQuantity) {
    } else {
    }
    
    this.updateBundleSummary();
  }

  getProductCardsForType(type) {
    // Find the collection section for this type
    const collectionSections = this.querySelectorAll('.collection-section');
    
    for (const section of collectionSections) {
      const title = section.querySelector('.collection-title')?.textContent.toLowerCase() || '';
      if ((type === 'wet' && title.includes('wet')) ||
          (type === 'dry' && title.includes('dry')) ||
          (type === 'treats' && title.includes('treat')) ||
          (type === 'litter' && title.includes('litter'))) {
        return Array.from(section.querySelectorAll('.product-card'));
      }
    }
    
    return [];
  }


  handleQuantityChange(button, change) {
    const productCard = button.closest('.product-card');
    const quantityInput = productCard.querySelector('.quantity-input');
    const productId = productCard.dataset.productId;
    const product = this.productList[productId];
    
    if (!product) return;
    
    let newQuantity = parseInt(quantityInput.value) + change;
    if (newQuantity < 0) newQuantity = 0;
    
    // Update input value explicitly
    quantityInput.value = newQuantity;
    quantityInput.setAttribute('value', newQuantity);
    
    // Show Lottie animation when adding items
    if (change > 0 && newQuantity > 0) {
      this.showQuantityAnimation(productCard);
    }
    
    // Force repaint to ensure visibility
    quantityInput.style.display = 'none';
    quantityInput.offsetHeight; // Trigger reflow
    quantityInput.style.display = '';
    
    this.updateSelection(product, newQuantity);
    this.updateBundleSummary();
    
  }

  updateSelection(product, quantity) {
    const productId = product.id.split('/').pop();
    const catKey = this.currentCat || 'cat1';
    
    // Initialize selections for this cat if needed
    if (!this.selections[catKey]) {
      this.selections[catKey] = [];
    }
    
    const catSelections = this.selections[catKey];
    const existingIndex = catSelections.findIndex(s => s.product_id === productId);
    
    if (quantity === 0) {
      if (existingIndex !== -1) {
        catSelections.splice(existingIndex, 1);
      }
    } else {
      const variant = product.variants.edges[0]?.node;
      const rechargeData = this.rechargeProducts[productId];
      
      const selectionData = {
        id: productId, // Add id field for unified products manager
        product_id: productId,
        variant_id: variant.id.split('/').pop(),
        variantId: variant.id.split('/').pop(), // Also add variantId for compatibility
        title: product.title,
        price: product.subscriptionPrice || parseFloat(variant.price.amount),
        quantity: quantity,
        type: this.getProductType(product),
        image: product.images.edges[0]?.node?.url,
        selling_plans: rechargeData?.selling_plan_groups?.[0]?.selling_plans || [],
        selling_plan_id: rechargeData?.selling_plan_groups?.[0]?.selling_plans?.[0]?.selling_plan_id,
        cat_number: catKey.replace('cat', '') // Store just the number (1, 2, 3, 4)
      };
      
      if (existingIndex !== -1) {
        catSelections[existingIndex] = selectionData;
      } else {
        catSelections.push(selectionData);
      }
    }
    
    // Save selections to localStorage after each update
    this.saveSelections();
  }

  getProductType(product) {
    const title = product.title.toLowerCase();
    
    // Find which collection this product belongs to
    for (const [type, products] of Object.entries(this.collections)) {
      if (products.some(p => p.id === product.id)) {
        return type;
      }
    }
    
    // Fallback to title detection
    if (title.includes('wet')) return 'wet';
    if (title.includes('treat')) return 'treats';
    if (title.includes('litter')) return 'litter';
    return 'dry';
  }

  findProductById(productId) {
    for (const type of Object.keys(this.collections)) {
      const product = this.collections[type].find(p => 
        p.id.split('/').pop() === productId
      );
      if (product) return product;
    }
    return null;
  }

  updateBundleSummary() {
    // Calculate totals
    let catSpecificPrice = 0;
    let totalPriceAllCats = 0;
    let totalItems = 0;
    let selectionsToUse = [];
    
    // Dispatch event for unified products manager
    // Handle both single cat and all cats mode
    if (this.currentCat && this.selections[this.currentCat]) {
      // Single cat mode
      const catNumber = parseInt(this.currentCat.replace('cat', ''));
      document.dispatchEvent(new CustomEvent('bundle-updated', {
        detail: {
          catNumber: catNumber,
          selections: this.selections[this.currentCat]
        }
      }));
    } else {
      // All cats mode - dispatch events for each cat that has selections
      Object.entries(this.selections).forEach(([catKey, catSelections]) => {
        if (catSelections && catSelections.length > 0) {
          const catNumber = parseInt(catKey.replace('cat', ''));
          document.dispatchEvent(new CustomEvent('bundle-updated', {
            detail: {
              catNumber: catNumber,
              selections: catSelections
            }
          }));
        }
      });
    }
    
    // Calculate price for all cats combined
    let allSelectionsForPrice = [];
    Object.values(this.selections).forEach(catSelections => {
      if (Array.isArray(catSelections)) {
        allSelectionsForPrice = allSelectionsForPrice.concat(catSelections);
      }
    });
    totalPriceAllCats = allSelectionsForPrice.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    // Calculate cat-specific values if in single-cat mode
    if (this.currentCat && this.selections[this.currentCat]) {
      // Single cat mode
      selectionsToUse = this.selections[this.currentCat];
      catSpecificPrice = selectionsToUse.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      totalItems = selectionsToUse.reduce((sum, item) => sum + item.quantity, 0);
    } else {
      // All cats mode
      selectionsToUse = allSelectionsForPrice;
      catSpecificPrice = totalPriceAllCats;
      totalItems = allSelectionsForPrice.reduce((sum, item) => sum + item.quantity, 0);
    }
    
    
    // Update bundle summary header (shows cat-specific in bundle modal)
    const bundleItemsCountEl = this.querySelector('.bundle-items-count');
    const bundlePriceValueEl = this.querySelector('.bundle-price-value');
    
    if (bundleItemsCountEl) bundleItemsCountEl.textContent = totalItems;
    if (bundlePriceValueEl) bundlePriceValueEl.textContent = `£${catSpecificPrice.toFixed(2)}`;
    
    // Update different price displays with appropriate values
    this.updatePriceDisplays(catSpecificPrice, totalPriceAllCats, totalItems);
    
    // Dispatch event for other components
    this.dispatchEvent(new CustomEvent('selectionsUpdated', {
      detail: { selections: selectionsToUse, totalPrice: totalPriceAllCats, totalItems }
    }));
  }

  updatePriceDisplays(catSpecificPrice, totalPriceAllCats, totalItems) {
    // Update subscription-collections price (shows cat-specific when editing)
    const subscriptionCollectionsPrices = document.querySelectorAll('.subscription-collections .bundle-total-price');
    subscriptionCollectionsPrices.forEach(el => {
      el.textContent = `£${catSpecificPrice.toFixed(2)}`;
    });
    
    // Update plan-pricing price (always shows total for all cats)
    const planPricingPrices = document.querySelectorAll('.plan-pricing .bundle-total-price');
    planPricingPrices.forEach(el => {
      el.textContent = `£${totalPriceAllCats.toFixed(2)}`;
    });

    // Update monthly tab price (always shows total for all cats)
    const monthlyTabPrice = document.querySelector('.tab[data-tab="monthly"] .bundle-total-price');
    if (monthlyTabPrice) {
      monthlyTabPrice.textContent = `£${totalPriceAllCats.toFixed(2)}`;
    }

    // Update step 7 if visible
    this.updateStep7BundlePrice(totalPriceAllCats, totalItems);
    
    // Update bundle system price in checkout bar
    this.updateBundleSystemPrice(totalPriceAllCats);
  }

  updateStep7BundlePrice(totalPrice, totalItems) {
    const step7 = document.querySelector('.quiz-step[data-step="7"]');
    if (!step7 || !step7.classList.contains('active')) {
      return; // Step 7 not visible, no need to update
    }
    
    // Update plan-pricing bundle total price elements in step 7 (shows total for all cats)
    const planPricingEls = step7.querySelectorAll('.plan-pricing .bundle-total-price');
    planPricingEls.forEach(price => {
      price.textContent = `£${totalPrice.toFixed(2)}`;
    });
    
    // Update bundle system price elements (monthly subscription price)
    const bundleSystemPriceEls = step7.querySelectorAll('.bundle-system-price');
    bundleSystemPriceEls.forEach(price => {
      price.textContent = `£${totalPrice.toFixed(2)}`;
    });
    
    // Update cat-specific item counts if in single-cat mode
    if (this.currentCat) {
      const catMealPlanSection = document.querySelector(`.meal-plan-section[data-cat="${this.currentCat}"]`);
      if (catMealPlanSection) {
        const catItemsCount = catMealPlanSection.querySelector('.total-items-count');
        if (catItemsCount) {
          // Calculate items for this specific cat
          const catSelections = this.selections[this.currentCat] || [];
          const catTotalItems = catSelections.reduce((sum, item) => sum + item.quantity, 0);
          catItemsCount.textContent = catTotalItems;
        }
      }
    } else {
      // In multi-cat mode when not editing a specific cat, only update non-cat-specific counts
      const totalItemsEls = document.querySelectorAll('.total-items-count:not([data-cat-items])');
      totalItemsEls.forEach(count => {
        count.textContent = totalItems;
      });
    }
    
    // Update delivery text based on current selections
    this.updateStep7DeliveryText();

    // Update plan summary price/day and "then" row
    const daysPerMonth = window.QuizConstants?.FOOD?.DAYS_PER_MONTH || 28;
    const pricePerDayEl = document.querySelector('.your-plan-summary__price-value');
    if (pricePerDayEl) {
      const pricePerDay = totalPrice / daysPerMonth;
      pricePerDayEl.textContent = `£${pricePerDay.toFixed(2)}/day`;
    }

    // Update monthly total
    const monthlyEl = document.querySelector('.your-plan-summary__monthly-value');
    if (monthlyEl) {
      monthlyEl.textContent = `£${totalPrice.toFixed(2)}`;
    }

    // Update "then" row with actual selection counts
    const thenEl = document.querySelector('.your-plan-summary__then-value');
    if (thenEl) {
      let totalCans = 0;
      let totalBags = 0;
      const allSelections = Object.values(this.selections).flat();
      allSelections.forEach(item => {
        if (item.type === 'dry') {
          totalBags += item.quantity || 0;
        } else if (item.type === 'wet') {
          totalCans += (item.quantity || 0) * (window.QuizConstants?.FOOD?.CANS_PER_PACK || 7);
        }
      });
      if (totalCans > 0 || totalBags > 0) {
        const parts = [];
        if (totalCans > 0) parts.push(`${totalCans} cans`);
        if (totalBags > 0) parts.push(`${totalBags} bag${totalBags !== 1 ? 's' : ''}`);
        thenEl.textContent = `${parts.join(' + ')} every ${daysPerMonth} days`;
      }
    }
  }

  updateBundleSystemPrice(totalPrice) {
    // Update bundle system price elements (monthly subscription price and checkout bar)
    const bundleSystemPriceEls = document.querySelectorAll('.bundle-system-price');
    bundleSystemPriceEls.forEach(price => {
      price.textContent = `£${totalPrice.toFixed(2)}`;
    });
    
    // Specifically update checkout bar price
    const checkoutBarPrice = document.querySelector('.checkout-bar-text .bundle-system-price');
    if (checkoutBarPrice) {
      checkoutBarPrice.textContent = `£${totalPrice.toFixed(2)}`;
    }
    
  }

  updateStep7DeliveryText() {
    // Calculate delivery text from current selections
    let bagsCount = 0;
    let tinsCount = 0;
    
    // Get selections based on current mode (same pattern as updateBundleSummary)
    let selectionsToUse = [];
    if (this.currentCat && this.selections[this.currentCat]) {
      selectionsToUse = this.selections[this.currentCat];
    } else {
      Object.values(this.selections).forEach(catSelections => {
        if (Array.isArray(catSelections)) {
          selectionsToUse = selectionsToUse.concat(catSelections);
        }
      });
    }
    
    selectionsToUse.forEach(item => {
      const collectionType = this.getCollectionTypeForItem(item);
      if (collectionType === 'dry') {
        bagsCount += item.quantity;
      } else if (collectionType === 'wet') {
        tinsCount += item.quantity * 7; // Each pack is 7 tins
      }
    });
    
    // Generate delivery text
    const parts = [];
    if (bagsCount > 0) {
      parts.push(`${bagsCount} bag${bagsCount > 1 ? 's' : ''}`);
    }
    if (tinsCount > 0) {
      parts.push(`${tinsCount} tin${tinsCount > 1 ? 's' : ''}`);
    }
    
    const deliveryText = parts.length > 0 ? parts.join(', ') + ' in total' : '0 items in total';
    
    // Update only the weekly tab delivery text elements (not monthly)
    const deliveryTextEls = document.querySelectorAll('[data-tab-content="trial"] .total-items-text');
    deliveryTextEls.forEach(el => {
      el.textContent = deliveryText;
    });
    
  }

  getCollectionTypeForItem(item) {
    // Determine collection type based on product title or type
    const title = item.title.toLowerCase();
    
    if (title.includes('wet') || title.includes('tin')) {
      return 'wet';
    } else if (title.includes('dry') || title.includes('chicken w/tuna') || title.includes('turkey')) {
      return 'dry';
    } else if (title.includes('treat')) {
      return 'treats';
    } else if (title.includes('litter')) {
      return 'litter';
    }
    
    // Fallback to type if available
    return item.type || 'unknown';
  }

  setupReturnToCartButton() {
    
    // Check if button already exists
    if (document.querySelector('.bundle-return-to-cart')) {
      return;
    }
    
    // Create return to cart button
    const returnBtn = document.createElement('button');
    returnBtn.className = 'bundle-return-to-cart button button--primary';
    returnBtn.innerHTML = 'Return to Cart';
    
    returnBtn.addEventListener('click', () => this.handleReturnToCart());
    
    // Add to bundle system header
    const bundleHeader = document.querySelector('.bundle-system-header');
    if (bundleHeader) {
      // Hide the close button
      const closeBtn = bundleHeader.querySelector('.bundle-system-close');
      if (closeBtn) {
        closeBtn.style.display = 'none';
      }
      
      // Add return button to header
      bundleHeader.appendChild(returnBtn);
    }
  }
  
  async handleReturnToCart() {
    
    // Validate minimum 1 item rule
    const totalItems = this.getTotalSelectedItems();
    if (totalItems === 0) {
      window.QuizUtils.Error.show('Please select at least 1 item for your monthly subscription');
      return;
    }
    
    // Get edit mode info
    const editMode = window.QuizState.get('editMode');
    if (!editMode || !editMode.quizId) {
      return;
    }
    
    try {
      // Show loading state
      const returnBtn = document.querySelector('.bundle-return-to-cart');
      if (returnBtn) {
        returnBtn.disabled = true;
        returnBtn.textContent = 'Updating cart...';
      }
      
      // Get current selections - only for the cat being edited in restricted mode
      let bundleItems;
      const isMultiCat = window.QuizState.get('submissionData.catCount') > 1;
      
      if (editMode.restricted && isMultiCat) {
        // In restricted edit mode, only get selections for the specific cat
        this.currentCat = `cat${editMode.catNumber}`;
        bundleItems = this.getSelections(false); // Get only current cat
      } else {
        // Normal mode or single cat - get all selections
        bundleItems = this.getSelections(true); // Get all cats
      }
      
      
      // Remove old bundle items accordingly
      console.log('Edit mode info:', {
        restricted: editMode.restricted,
        isMultiCat: isMultiCat,
        catNumber: editMode.catNumber,
        quizId: editMode.quizId
      });
      
      if (editMode.restricted && isMultiCat) {
        // Only remove bundle items for the specific cat being edited
        const catNumberStr = String(editMode.catNumber); // Ensure it's a string
        await window.QuizCartAPI.removeCartItemsByQuizId(editMode.quizId, 'bundle', catNumberStr);
      } else {
        // Single cat mode or non-restricted - remove all bundle items
        await window.QuizCartAPI.removeCartItemsByQuizId(editMode.quizId, 'bundle');
      }
      
      // Calculate total bundle price
      const bundlePrice = bundleItems.reduce((sum, item) => {
        return sum + (item.price * item.quantity);
      }, 0);
      
      // Count wet and dry items for summary
      let bundleWetCount = 0;
      let bundleDryCount = 0;
      bundleItems.forEach(item => {
        if (item.type === 'wet') bundleWetCount += item.quantity;
        else if (item.type === 'dry') bundleDryCount += item.quantity;
      });
      
      // Get Recharge bundle items to ensure we have the _rc_bundle properties
      let rechargeBundleItems;
      try {
        console.log('🔵 EDIT MODE: Getting Recharge bundle items...');
        rechargeBundleItems = await this.getRechargeBundleItems(bundleItems);
        console.log('✅ EDIT MODE: Got Recharge bundle items with properties:', rechargeBundleItems);
      } catch (error) {
        console.error('❌ EDIT MODE: Failed to get Recharge bundle items:', error);
        // Fall back to regular items if Recharge fails
        rechargeBundleItems = bundleItems;
      }
      
      // Get submission data
      const submissionData = window.QuizState.get('submissionData') || {};
      
      // Build quiz cart items using the API function (will apply condensed format)
      // The API function will handle Recharge properties preservation
      // Parameters: submissionData, bundleItems, trailItems, extrasItems, isEditMode, editQuizId
      const cartItems = await window.QuizCartAPI.buildQuizCartItems(
        submissionData,         // submission data
        rechargeBundleItems,    // bundle items with Recharge properties
        [],                     // trail items (empty for bundle-only edit)
        [],                     // extras items (empty for bundle-only edit)
        true,                   // isEditMode
        editMode.quizId         // editQuizId
      );
      
      console.log('📦 EDIT MODE: Final cart items with condensed format:', cartItems);
      
      // Add new items to cart
      await window.QuizCartAPI.addItems(cartItems);
      
      // Clear edit mode data
      localStorage.removeItem(`edit_${editMode.quizId}_data`);
      localStorage.removeItem(`edit_${editMode.quizId}_products`);
      
      // Redirect to cart
      window.location.href = '/cart';
      
    } catch (error) {
      window.QuizUtils.Error.show('Error updating cart. Please try again.');
      
      // Re-enable button
      const returnBtn = document.querySelector('.bundle-return-to-cart');
      if (returnBtn) {
        returnBtn.disabled = false;
        returnBtn.textContent = 'Return to Cart';
      }
    }
  }
  
  getTotalSelectedItems() {
    let total = 0;
    Object.values(this.selections).forEach(catSelections => {
      if (Array.isArray(catSelections)) {
        total += catSelections.reduce((sum, item) => sum + item.quantity, 0);
      }
    });
    return total;
  }

  render() {
    this.renderCollections();
  }

  renderCollections() {
    const collectionsContainer = this.querySelector('.collections-container');
    
    if (!collectionsContainer) {
      return;
    }

    if (!this.isReady() && Object.keys(this.collections).length === 0) {
      collectionsContainer.innerHTML = '<div class="bundle-loading">Loading products...</div>';
      return;
    }

    collectionsContainer.innerHTML = '';
    
    // Render collections in order: Dry Food, Wet Food, Treats, Litter
    const collectionOrder = [
      { type: 'dry', name: 'Dry Food' },
      { type: 'wet', name: 'Wet Food' },
      { type: 'treats', name: 'Treats' },
      { type: 'litter', name: 'Litter' }
    ];

    for (const collection of collectionOrder) {
      if (this.collections[collection.type] && this.collections[collection.type].length > 0) {
        const collectionEl = this.createCollectionElement(collection);
        collectionsContainer.appendChild(collectionEl);
      }
    }
  }

  createCollectionElement(collection) {
    const collectionEl = document.createElement('div');
    collectionEl.className = 'collection-section';
    collectionEl.innerHTML = `
      <h3 class="collection-title Md_Body_Bold">${collection.name}</h3>
      <div class="products-grid"></div>
    `;
    
    const productsGrid = collectionEl.querySelector('.products-grid');
    
    for (const product of this.collections[collection.type]) {
      const productEl = this.createProductElement(product);
      productsGrid.appendChild(productEl);
    }
    
    return collectionEl;
  }

  createProductElement(product) {
    const productEl = document.createElement('div');
    productEl.className = 'product-card';
    const productId = product.id.split('/').pop();
    productEl.dataset.productId = productId;
    
    const imageUrl = product.images.edges[0]?.node?.url || '';
    const imageAlt = product.title;
    
    // Check if product is already selected for current cat
    let quantity = 0;
    if (this.currentCat && this.selections[this.currentCat]) {
      const selection = this.selections[this.currentCat].find(s => s.product_id === productId);
      quantity = selection ? selection.quantity : 0;
    }
    
    // Get pricing info
    const variant = product.variants?.edges?.[0]?.node;
    const subscriptionPrice = product.subscriptionPrice || (variant ? parseFloat(variant.price.amount) : 0);
    
    // Format price to 2 decimal places
    const formattedPrice = subscriptionPrice.toFixed(2);
    
    productEl.innerHTML = `
      <img class="product-image" src="${imageUrl}" alt="${imageAlt}" />
      <div class="product-info-area">
        <div>
          <div class="product-title">${product.title}</div>
          <div class="product-price">£${formattedPrice}</div>
        </div>
        <div class="quantity-controls">
          <button class="quantity-btn minus-btn" type="button">
            <svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1H11" stroke="white" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
          <input class="quantity-input" type="number" value="${quantity}" min="0" />
          <button class="quantity-btn plus-btn" type="button">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 1V11M1 6H11" stroke="white" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      </div>
    `;
    
    // Store product data for easy access
    this.productList = this.productList || {};
    this.productList[productId] = product;
    
    return productEl;
  }


  isReady() {
    return !this.loadingStates.auth && 
           !this.loadingStates.collections && 
           !this.loadingStates.products;
  }

  // Public methods for external access
  getSelections(returnAllCats = false) {
    // If explicitly requested to return all cats, or if in multi-cat mode
    const isMultiCat = window.QuizState.get('submissionData.catCount') > 1;
    const actualCatCount = window.QuizState.get('submissionData.catCount') || 1;
    
    console.log('getSelections called:', {
      returnAllCats,
      isMultiCat,
      actualCatCount,
      currentCat: this.currentCat,
      selectionsKeys: Object.keys(this.selections)
    });
    
    if (returnAllCats || (isMultiCat && !this.currentCat)) {
      // Return all selections combined with cat number info preserved
      let allSelections = [];
      
      // Check if we're in edit mode
      const editMode = window.QuizState.get('editMode');
      const isEditMode = editMode?.active;
      
      Object.entries(this.selections).forEach(([catKey, catSelections]) => {
        // Extract cat number from key (e.g., 'cat2' -> 2)
        const catNumber = parseInt(catKey.replace('cat', ''));
        
        // In edit mode, include the cat being edited regardless of actualCatCount
        // Otherwise, only include selections for cats that actually exist
        if ((isEditMode && catSelections.length > 0) || (catNumber <= actualCatCount && Array.isArray(catSelections))) {
          // Ensure each selection has the cat_number property
          catSelections.forEach(selection => {
            allSelections.push({
              ...selection,
              cat_number: catNumber.toString()
            });
          });
        }
      });
      return allSelections;
    }
    
    // Single cat mode or specific cat requested
    if (this.currentCat && this.selections[this.currentCat]) {
      const catNumber = this.currentCat.replace('cat', '');
      return this.selections[this.currentCat].map(selection => ({
        ...selection,
        cat_number: catNumber
      }));
    }
    
    // Handle single cat mode when currentCat is null
    // Check for 'cat1' or '1' for backwards compatibility
    const catKey = this.selections['cat1'] ? 'cat1' : (this.selections['1'] ? '1' : null);
    if (catKey && this.selections[catKey]) {
      const catNumber = catKey.replace('cat', '') || '1';
      return this.selections[catKey].map(selection => ({
        ...selection,
        cat_number: catNumber
      }));
    }
    
    return [];
  }

  getTotalPrice() {
    // Always return the combined price for all cats
    let allSelections = [];
    Object.values(this.selections).forEach(catSelections => {
      if (Array.isArray(catSelections)) {
        allSelections = allSelections.concat(catSelections);
      }
    });
    return allSelections.reduce((sum, item) => 
      sum + (item.price * item.quantity), 0
    );
  }

  reset() {
    this.selections = {};
    this.currentCat = null;
    this.recommendations = null;
    this.render();
  }
  
  // Method to switch to a specific cat
  switchToCat(catNumber) {
    const prevCat = this.currentCat;
    this.currentCat = `cat${catNumber}`;
    
    // Initialize selections for this cat if needed
    if (!this.selections[this.currentCat]) {
      this.selections[this.currentCat] = [];
    }
    
    // Clear all quantity inputs first to ensure clean state
    const allQuantityInputs = this.querySelectorAll('.quantity-input');
    allQuantityInputs.forEach(input => {
      input.value = 0;
    });
    
    // Re-render to show this cat's selections
    this.render();
    this.updateBundleSummary();
  }
  
  saveSelections() {
    // Save bundle selections to localStorage
    const step10Data = JSON.parse(localStorage.getItem('step10Selections') || '{}');
    step10Data.bundle = this.selections;
    step10Data.timestamp = Date.now();
    localStorage.setItem('step10Selections', JSON.stringify(step10Data));
  }
  
  loadSelections() {
    // Load bundle selections from localStorage
    const step10Data = JSON.parse(localStorage.getItem('step10Selections') || '{}');
    if (step10Data.bundle) {
      this.selections = step10Data.bundle;
      return true;
    }
    return false;
  }
  
  restoreUIFromSelections() {
    // Wait for products to be loaded before restoring UI
    if (!this.isReady()) {
      setTimeout(() => this.restoreUIFromSelections(), 500);
      return;
    }
    
    
    // For single cat mode, ensure currentCat is set
    if (!this.currentCat && window.QuizState.get('submissionData.catCount') === 1) {
      this.currentCat = 'cat1';
    }
    
    this.dropHiddenFromSelections();

    const catKey = this.currentCat || 'cat1';
    const catSelections = this.selections[catKey] || [];
    
    
    // Clear all inputs first
    const allQuantityInputs = this.querySelectorAll('.quantity-input');
    allQuantityInputs.forEach(input => {
      input.value = 0;
    });
    
    // Restore each saved selection
    catSelections.forEach(selection => {
      const productCard = this.querySelector(`[data-product-id="${selection.product_id}"]`);
      if (productCard) {
        const quantityInput = productCard.querySelector('.quantity-input');
        if (quantityInput) {
          quantityInput.value = selection.quantity;
        }
      }
    });
    
    // Update summary
    this.updateBundleSummary();
  }
  
  showQuantityAnimation(productCard) {
    if (!productCard || typeof lottie === 'undefined') return;
    
    const quantityControls = productCard.querySelector('.quantity-controls');
    if (!quantityControls) return;
    
    // Check if animation container already exists
    let animationContainer = quantityControls.querySelector('.quantity-lottie-overlay');
    
    if (!animationContainer) {
      // Create animation container
      animationContainer = document.createElement('div');
      animationContainer.className = 'quantity-lottie-overlay';
      quantityControls.appendChild(animationContainer);
    }
    
    // Clear any existing animation
    animationContainer.innerHTML = '';
    
    // Load and play Lottie animation
    const animation = lottie.loadAnimation({
      container: animationContainer,
      renderer: 'svg',
      loop: false,
      autoplay: false,
      path: window.quantitySelectorAnimationUrl || '/assets/quantity-selector-ani.json'
    });
    
    // Show animation
    animationContainer.classList.add('active');
    animation.play();
    
    // Hide animation after it completes
    animation.addEventListener('complete', () => {
      animationContainer.classList.remove('active');
      setTimeout(() => {
        animation.destroy();
      }, 200); // Wait for fade out transition
    });
  }

  async getRechargeBundleItems(selections = null) {
    console.log('🔵 RECHARGE BUNDLE: Starting getRechargeBundleItems');
    console.log('🔵 RECHARGE BUNDLE: Input selections:', selections);
    
    try {
      // Get parent bundle product details
      const parentProductId = this.getAttribute('bundle-product-id');
      const parentVariantId = this.getAttribute('bundle-variant-id');
      const parentHandle = this.getAttribute('bundle-handle');
      
      console.log('🔵 RECHARGE BUNDLE: Parent product details:', {
        parentProductId,
        parentVariantId,
        parentHandle
      });
      
      // Use provided selections or get current selections
      const bundleSelections = selections || this.getCurrentSelections();
      
      console.log('🔵 RECHARGE BUNDLE: Bundle selections to process:', bundleSelections);
      
      // Transform selections to Recharge format
      const rechargeSelections = bundleSelections.map(item => {
        const selection = {
          collectionId: this.getCollectionIdForProduct(item),
          externalProductId: String(item.product_id || item.id),
          externalVariantId: String(item.variant_id || item.variantId),
          quantity: item.quantity
        };
        
        // Add selling plan if available
        if (item.selling_plan_id) {
          selection.sellingPlan = item.selling_plan_id;
        }
        
        console.log('🔵 RECHARGE BUNDLE: Transformed selection:', selection);
        return selection;
      });
      
      // Build the bundle object for Recharge
      const bundle = {
        externalVariantId: String(parentVariantId),
        externalProductId: String(parentProductId),
        selections: rechargeSelections
      };
      
      console.log('🔵 RECHARGE BUNDLE: Bundle object for validation:', bundle);
      
      // Validate the bundle
      console.log('🔵 RECHARGE BUNDLE: Calling recharge.bundle.validateDynamicBundle...');
      const isValid = await recharge.bundle.validateDynamicBundle(bundle);
      console.log('🔵 RECHARGE BUNDLE: Bundle validation result:', isValid);
      
      if (!isValid) {
        console.error('❌ RECHARGE BUNDLE: Bundle validation failed!');
        throw new Error('Bundle validation failed');
      }
      
      // Get Recharge bundle items
      console.log('🔵 RECHARGE BUNDLE: Calling recharge.bundle.getDynamicBundleItems...');
      const rechargeBundleItems = await recharge.bundle.getDynamicBundleItems(bundle, parentHandle);
      
      console.log('🔵 RECHARGE BUNDLE: Raw Recharge bundle items:', rechargeBundleItems);
      
      // Log each item's properties
      rechargeBundleItems.forEach((item, index) => {
        console.log(`🔵 RECHARGE BUNDLE: Item ${index + 1} properties:`, {
          id: item.id,
          quantity: item.quantity,
          properties: item.properties,
          selling_plan: item.selling_plan,
          _rc_bundle: item.properties?._rc_bundle,
          _rc_bundle_variant: item.properties?._rc_bundle_variant,
          _rc_bundle_parent: item.properties?._rc_bundle_parent,
          _rc_bundle_collection_id: item.properties?._rc_bundle_collection_id
        });
      });
      
      // Merge Recharge properties with original selections
      const mergedItems = bundleSelections.map((selection, index) => {
        const rechargeItem = rechargeBundleItems[index];
        
        if (!rechargeItem) {
          console.warn(`⚠️ RECHARGE BUNDLE: No Recharge item for selection ${index}`);
          return selection;
        }
        
        // Extract numeric variant ID from GraphQL ID if needed
        let variantId = rechargeItem.id;
        if (variantId && variantId.startsWith('gid://')) {
          variantId = variantId.split('/').pop();
          console.log(`🔵 RECHARGE BUNDLE: Extracted numeric ID from GraphQL: ${rechargeItem.id} -> ${variantId}`);
        }
        
        // Merge the Recharge properties into the selection
        const mergedItem = {
          ...selection,
          id: variantId || selection.variant_id || selection.variantId,
          variant_id: variantId || selection.variant_id || selection.variantId,
          variantId: variantId || selection.variant_id || selection.variantId,
          quantity: selection.quantity,
          properties: {
            ...selection.properties,
            ...rechargeItem.properties
          }
        };
        
        // Add selling plan if present
        if (rechargeItem.selling_plan) {
          mergedItem.selling_plan = rechargeItem.selling_plan;
        }
        
        console.log(`🔵 RECHARGE BUNDLE: Merged item ${index + 1}:`, {
          id: mergedItem.id,
          variant_id: mergedItem.variant_id,
          has_rc_bundle: !!mergedItem.properties._rc_bundle,
          _rc_bundle: mergedItem.properties._rc_bundle
        });
        
        return mergedItem;
      });
      
      console.log('✅ RECHARGE BUNDLE: Final merged items with Recharge properties:', mergedItems);
      
      return mergedItems;
      
    } catch (error) {
      console.error('❌ RECHARGE BUNDLE: Error in getRechargeBundleItems:', error);
      console.error('❌ RECHARGE BUNDLE: Error stack:', error.stack);
      
      // Return original selections if Recharge fails
      console.warn('⚠️ RECHARGE BUNDLE: Falling back to original selections without Recharge properties');
      return selections || this.getCurrentSelections();
    }
  }
  
  getCollectionIdForProduct(item) {
    // Map product types to collection IDs
    const type = item.type || this.getCollectionTypeForItem(item);
    const collectionMap = {
      'wet': this.getAttribute('wet-collection-id') || window.QuizConstants?.COLLECTIONS?.WET_FOOD,
      'dry': this.getAttribute('dry-collection-id') || window.QuizConstants?.COLLECTIONS?.DRY_FOOD,
      'treats': this.getAttribute('treats-collection-id') || window.QuizConstants?.COLLECTIONS?.TREATS,
      'litter': this.getAttribute('litter-collection-id') || window.QuizConstants?.COLLECTIONS?.LITTER
    };
    
    const collectionId = collectionMap[type];
    console.log(`🔵 RECHARGE BUNDLE: Collection ID for ${item.title} (type: ${type}):`, collectionId);
    
    return collectionId;
  }
  
  getCurrentSelections() {
    // Get all current selections across all cats
    const allSelections = [];
    
    Object.entries(this.selections).forEach(([catKey, catSelections]) => {
      catSelections.forEach(selection => {
        allSelections.push({
          ...selection,
          cat_number: catKey.replace('cat', '')
        });
      });
    });
    
    console.log('🔵 RECHARGE BUNDLE: Current selections from bundle builder:', allSelections);
    return allSelections;
  }
}

// Register custom element
customElements.define('quiz-bundle', QuizSubscriptionBuilder);